'use client'

import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useMemo } from 'react'
import { Organization, WebApplication, WebSite, WithContext } from 'schema-dts'
import { getDomain } from '@/setup/domain'
import { Locale, parseLanguageFromLocale, parseRegionFromLocale } from '@/setup/locales'

export const NeotasteStructuredData = () => {
    const t = useTranslations()
    const locale = useLocale()

    const currentRegion = parseRegionFromLocale(locale).toLowerCase()
    const currentLanguage = parseLanguageFromLocale(locale)

    const organizationJsonLd = useMemo<WithContext<Organization>>(
        () => ({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            url: getDomain('base'),
            // logo: '/_images/meta/icon_400x400.png',
            name: 'NeoTaste',
            description:
                'Discover and try new restaurants with NeoTaste. 2-for-1 main course, an appetizer with the main course on the house, and other cool deals!',
            sameAs: [
                'https://www.tiktok.com/@neotaste.app',
                'https://www.linkedin.com/company/neotaste/',
                'https://www.facebook.com/neotasteapp/',
                'https://www.instagram.com/neotaste.app',
            ],
        }),
        []
    )

    const websiteJsonLd = useMemo<WithContext<WebSite>>(
        () => ({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: getDomain(locale as Locale),
            name: 'NeoTaste',
            inLanguage: locale,
        }),
        [locale]
    )

    const iOSAppJsonLd = useMemo<WithContext<WebApplication>>(
        () => ({
            '@id': t('shared.appStoreDownloadUrl', { region: currentRegion }),
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'NeoTaste',
            applicationCategory: 'Food & Drink',
            operatingSystem: 'iOS',
            url: t('shared.appStoreDownloadUrl', { region: currentRegion }),
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: 4.8,
                ratingCount: 8606,
            },
        }),
        [t, currentRegion]
    )

    const playStoreAppJsonLd = useMemo<WithContext<WebApplication>>(
        () => ({
            '@id': t('shared.playStoreDownloadUrl', {
                region: currentRegion,
                lang: currentLanguage,
            }),
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'NeoTaste',
            applicationCategory: 'Food & Drink',
            operatingSystem: 'Android',
            url: t('shared.playStoreDownloadUrl', {
                region: currentRegion,
                lang: currentLanguage,
            }),
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: 4.5,
                ratingCount: 2280,
            },
        }),
        [t, currentRegion, currentLanguage]
    )

    return (
        <Fragment>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(organizationJsonLd),
                }}
            />

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(websiteJsonLd),
                }}
            />

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(iOSAppJsonLd),
                }}
            />

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(playStoreAppJsonLd),
                }}
            />
        </Fragment>
    )
}
